import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Cloud, CloudRain, CloudSnow, CloudDrizzle, CloudLightning, CloudFog, Sun, Wind, Settings, Loader, X } from 'lucide-react'
import { widgetAPI, weatherAPI } from '../../services/api'
import { getPreferredZipCode, savePreferredZipCode } from '../../utils/zipCodePreference'
import { useModalClose } from '../../hooks/useModalClose'
import toast from 'react-hot-toast'

interface WeatherWidgetProps {
  widget: any
  isExpanded: boolean
}

interface WeatherData {
  location: string
  temperature: number
  condition: string
  humidity: number
  windSpeed: number
  forecast: Array<{
    day: string
    date: string
    high: number
    low: number
    condition: string
  }>
}

export default function WeatherWidget({ widget, isExpanded }: WeatherWidgetProps) {
  const [showSettings, setShowSettings] = useState(false)
  const [zipCode, setZipCode] = useState(widget.config?.zipCode || getPreferredZipCode())
  const [weather, setWeather] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Modal close handler
  useModalClose(() => setShowSettings(false))
  
  useEffect(() => {
    fetchWeatherData()
  }, [widget.config?.zipCode])
  
  const fetchWeatherData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const currentZip = widget.config?.zipCode || getPreferredZipCode()
      
      // Fetch real weather data from backend API
      const { data } = await weatherAPI.getByZip(currentZip)
      setWeather(data)
      setLoading(false)
    } catch (err) {
      console.error('Failed to fetch weather:', err)
      setError('Failed to load weather data')
      setLoading(false)
      
      // Fallback to generated data if API fails
      const weatherData = generateWeatherData(currentZip)
      setWeather(weatherData)
      setLoading(false)
    }
  }
  
  const generateWeatherData = (zip: string): WeatherData => {
    // Generate realistic weather data based on zip code and geography
    const zipPrefix = parseInt(zip.substring(0, 2)) || 0
    const seed = zip.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    
    // Determine geographic region and typical weather based on ZIP prefix
    // This provides more realistic temperature ranges for different US regions
    let baseTemp = 65
    let tempRange = 15
    let location = `ZIP ${zip}`
    
    // ZIP code geography (rough US regions)
    if (zipPrefix >= 0 && zipPrefix <= 9) {
      // Northeast (NY, PA, NJ, etc.) - 11757 is in NY
      baseTemp = 55
      tempRange = 20
      location = `${zip}, NY`
    } else if (zipPrefix >= 10 && zipPrefix <= 19) {
      // Northeast/Mid-Atlantic
      baseTemp = 58
      tempRange = 18
    } else if (zipPrefix >= 20 && zipPrefix <= 29) {
      // Southeast
      baseTemp = 72
      tempRange = 12
    } else if (zipPrefix >= 30 && zipPrefix <= 39) {
      // Southeast/Deep South
      baseTemp = 75
      tempRange = 15
    } else if (zipPrefix >= 40 && zipPrefix <= 49) {
      // Midwest
      baseTemp = 62
      tempRange = 22
    } else if (zipPrefix >= 50 && zipPrefix <= 59) {
      // Central Plains
      baseTemp = 65
      tempRange = 20
    } else if (zipPrefix >= 60 && zipPrefix <= 69) {
      // Midwest/Great Lakes
      baseTemp = 58
      tempRange = 20
    } else if (zipPrefix >= 70 && zipPrefix <= 79) {
      // South Central
      baseTemp = 78
      tempRange = 15
    } else if (zipPrefix >= 80 && zipPrefix <= 89) {
      // Mountain West
      baseTemp = 68
      tempRange = 25
    } else if (zipPrefix >= 90 && zipPrefix <= 99) {
      // West Coast/Pacific
      baseTemp = 68
      tempRange = 12
    }
    
    // Get current month for seasonal adjustment
    const currentMonth = new Date().getMonth() // 0-11
    let seasonalAdjust = 0
    
    // Seasonal temperature adjustment
    if (currentMonth >= 11 || currentMonth <= 1) { // Winter
      seasonalAdjust = -15
    } else if (currentMonth >= 2 && currentMonth <= 4) { // Spring
      seasonalAdjust = 0
    } else if (currentMonth >= 5 && currentMonth <= 8) { // Summer
      seasonalAdjust = +15
    } else { // Fall
      seasonalAdjust = +5
    }
    
    const adjustedTemp = baseTemp + seasonalAdjust
    
    const conditions = ['sunny', 'cloudy', 'rainy', 'partly-cloudy']
    const conditionNames = ['Sunny', 'Cloudy', 'Rainy', 'Partly Cloudy']
    const currentCondition = seed % 4
    
    // Get current day and next 4 days
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const today = new Date()
    const forecast = []
    
    for (let i = 0; i < 5; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      const dayIndex = date.getDay()
      const dayName = i === 0 ? 'Today' : days[dayIndex]
      
      // Daily variation
      const dayVariation = ((seed + i * 7) % tempRange) - (tempRange / 2)
      
      forecast.push({
        day: dayName,
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        high: Math.round(adjustedTemp + dayVariation + 5),
        low: Math.round(adjustedTemp + dayVariation - 8),
        condition: conditions[(currentCondition + i) % 4]
      })
    }
    
    return {
      location,
      temperature: forecast[0].high - 3,
      condition: conditionNames[currentCondition],
      humidity: 45 + (seed % 45),
      windSpeed: 3 + (seed % 15),
      forecast
    }
  }

  const handleSaveZipCode = async () => {
    try {
      console.log('Saving ZIP code:', zipCode, 'for widget:', widget.id)
      const response = await widgetAPI.update(widget.id, { config_data: JSON.stringify({ zipCode }) })
      console.log('ZIP code saved, response:', response)
      
      // Update the widget config locally
      widget.config = { zipCode }
      
      // Save to shared preference for other widgets
      savePreferredZipCode(zipCode)
      
      // Fetch new weather data for the updated zip code
      await fetchWeatherData()
      
      toast.success('ZIP code updated!')
      setShowSettings(false)
    } catch (error: any) {
      console.error('Failed to save ZIP code:', error)
      console.error('Error response:', error.response?.data)
      toast.error(error.response?.data?.error || 'Failed to save ZIP code')
    }
  }

  const getWeatherIcon = (condition: string, size: number = 32) => {
    const lowerCondition = condition.toLowerCase()
    
    // Clear/Sunny
    if (lowerCondition.includes('clear') || lowerCondition.includes('sunny')) {
      return <Sun size={size} className="weather-icon-sunny" style={{ color: '#F59E0B' }} />
    }
    
    // Partly Cloudy
    if (lowerCondition.includes('partly') || lowerCondition.includes('few')) {
      return <Cloud size={size} style={{ color: 'var(--color-text-secondary)', opacity: 0.7 }} />
    }
    
    // Cloudy/Overcast
    if (lowerCondition.includes('cloudy') || lowerCondition.includes('overcast')) {
      return <Cloud size={size} style={{ color: 'var(--color-text-secondary)' }} />
    }
    
    // Thunderstorm
    if (lowerCondition.includes('thunder') || lowerCondition.includes('storm')) {
      return <CloudLightning size={size} style={{ color: '#9333EA' }} />
    }
    
    // Snow
    if (lowerCondition.includes('snow') || lowerCondition.includes('flurr') || lowerCondition.includes('sleet')) {
      return <CloudSnow size={size} style={{ color: '#93C5FD' }} />
    }
    
    // Drizzle/Light Rain
    if (lowerCondition.includes('drizzle') || lowerCondition.includes('shower')) {
      return <CloudDrizzle size={size} style={{ color: '#60A5FA' }} />
    }
    
    // Rain/Heavy Rain
    if (lowerCondition.includes('rain') || lowerCondition.includes('precip')) {
      return <CloudRain size={size} style={{ color: '#3B82F6' }} />
    }
    
    // Fog/Mist
    if (lowerCondition.includes('fog') || lowerCondition.includes('mist') || lowerCondition.includes('haze')) {
      return <CloudFog size={size} style={{ color: 'var(--color-text-secondary)', opacity: 0.6 }} />
    }
    
    // Default to cloudy
    return <Cloud size={size} style={{ color: 'var(--color-text-secondary)' }} />
  }


  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader className="animate-spin text-blue-600" size={32} />
      </div>
    )
  }

  if (error || !weather) {
    return (
      <div className="h-full flex items-center justify-center text-center p-4">
        <div>
          <p className="text-red-600 mb-2">{error || 'Failed to load weather'}</p>
          <button
            onClick={fetchWeatherData}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col relative">
      <button
        onClick={() => setShowSettings(true)}
        className="absolute top-0 right-0 p-2 text-gray-400 hover:text-gray-600 transition"
      >
        <Settings size={16} />
      </button>

      <div className="pt-4 mb-4">
        {/* 3-column layout: Humidity | Temperature | Wind */}
        <div className="grid grid-cols-3 gap-2 items-center mb-3">
          {/* Left: Humidity */}
          <div className="text-center">
            <Cloud size={20} style={{ color: 'var(--color-text-secondary)' }} className="mx-auto mb-1" />
            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Humidity</div>
            <div className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{weather.humidity}%</div>
          </div>
          
          {/* Center: Temperature (large) */}
          <div className="text-center">
            <div className="flex justify-center mb-2">
              {getWeatherIcon(weather.condition, 48)}
            </div>
            <div className="text-6xl font-bold mt-3" style={{ color: 'var(--color-text-primary)' }}>{weather.temperature}°</div>
          </div>
          
          {/* Right: Wind */}
          <div className="text-center">
            <Wind size={20} style={{ color: 'var(--color-text-secondary)' }} className="mx-auto mb-1" />
            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Wind</div>
            <div className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{weather.windSpeed} mph</div>
          </div>
        </div>
        
        {/* Condition and Location below */}
        <div className="text-center">
          <div className="text-lg mb-1" style={{ color: 'var(--color-text-secondary)' }}>{weather.condition}</div>
          <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{weather.location}</div>
        </div>
      </div>

      <div className={isExpanded ? 'flex-1 px-4' : 'px-4'}>
        <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>5-Day Forecast</h4>
        <div className={isExpanded ? 'space-y-2' : 'flex gap-2 overflow-x-auto'}>
          {weather.forecast.map((day, idx) => (
            <div
              key={`${day.day}-${idx}`}
              className={
                isExpanded
                  ? 'flex items-center justify-between p-3 rounded-lg'
                  : 'flex-shrink-0 p-2 rounded-lg text-center min-w-[60px]'
              }
              style={{ backgroundColor: 'var(--color-surface)' }}
            >
              {isExpanded ? (
                <>
                  <div className="w-20">
                    <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{day.day}</div>
                    <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{day.date}</div>
                  </div>
                  {getWeatherIcon(day.condition, 32)}
                  <div className="flex gap-2 items-center">
                    <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{day.high}°</span>
                    <span style={{ color: 'var(--color-text-secondary)' }}>{day.low}°</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-xs font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>{day.day}</div>
                  <div className="flex justify-center mb-1">
                    {getWeatherIcon(day.condition, 24)}
                  </div>
                  <div className="text-xs">
                    <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{day.high}°</span>
                    <span style={{ color: 'var(--color-text-secondary)' }}>/{day.low}°</span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSettings(false)}
        >
          <div 
            className="rounded-lg shadow-2xl w-full max-w-md mx-4"
            style={{ 
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <h3 className="text-xl font-semibold">Weather Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-base font-medium mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="Enter ZIP code"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)',
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 px-4 py-2 rounded-lg transition font-medium"
                  style={{ 
                    backgroundColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveZipCode}
                  className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition font-medium"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

